import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants/api';
import type { UserRegistrationDetails } from '../types/Registration';
import type { Invite } from '../types/User';
import supabase from '../utils/supabase';

interface UseInvitationRegistrationResult {
  fetchInvitation: (token: string) => Promise<{ 
    success: boolean; 
    invitation?: Invite; 
    error?: string;
  }>;

  registerAndAcceptInvitation: (userDetails: UserRegistrationDetails, invitationToken: string) => Promise<{ 
    success: boolean; 
    token?: string; 
    error?: string;
    message?: string;
  }>;
  isLoading: boolean;
  error: string | null;
}

// Hook for invitation-based registration that combines user creation and invitation acceptance
export const useInvitationRegistration = (): UseInvitationRegistrationResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInvitation = useCallback(async (token: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/invitations/accept?token=${token}`);
      const invitation = response.data.returnInvitation;
      let agency = null;
      
      // If agency details are available, use them; otherwise, fallback to agencyId string
      if (invitation.agencyId) {
        agency = invitation.agencyId;
      }
      
      return { 
        success: true, 
        invitation,
        agency: agency || undefined
      };
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Invalid or expired invitation.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const registerAndAcceptInvitation = useCallback(async (userDetails: UserRegistrationDetails, invitationToken: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // Single API call that handles both user creation AND invitation acceptance
      const response = await axios.post(`${API_BASE_URL}/api/invitations/accept`, {
        token: invitationToken,

        // Core Identity
        surname: userDetails.surname,
        fullNames: userDetails.fullNames,
        nickname: userDetails.nickname,
        password: userDetails.password,
        idNumber: userDetails.idNumber,
        identificationType: userDetails.identificationType,
        dateOfBirth: userDetails.dateOfBirth,
        gender: userDetails.gender,
        
        // Contact
        mobileNumber: userDetails.mobileNumber,
        homeTelephone: userDetails.homeTelephone,
        
        // Physical Address
        unitNumber: userDetails.unitNumber,
        complex: userDetails.complex,
        streetNumber: userDetails.streetNumber,
        streetName: userDetails.streetName,
        suburb: userDetails.suburb,
        city: userDetails.city,
        postalCode: userDetails.postalCode,
        
        // Postal Address
        postalAddressSameAsResidential: userDetails.postalAddressSameAsResidential,
        postalUnitNumber: userDetails.postalUnitNumber,
        postalComplex: userDetails.postalComplex,
        postalStreetNumber: userDetails.postalStreetNumber,
        postalStreetName: userDetails.postalStreetName,
        postalSuburb: userDetails.postalSuburb,
        postalCity: userDetails.postalCity,
        postalPostalCode: userDetails.postalPostalCode,
        
        // Legal Agreements
        agreedTerms: userDetails.agreedTerms,
        agreedPOPIA: userDetails.agreedPOPIA,
      });
      
      const responseMessage = response.data.message || 'Registration and invitation acceptance successful!';
      
      const token = response.data.session?.access_token;
      const refreshToken = response.data.session?.refresh_token;
      if (token) {
        sessionStorage.setItem('token', token);
        // Hand the session to the Supabase client so its background refresh
        // manages it going forward — without this the client never learns a
        // session exists, and the token hard-expires in 1hr with no renewal.
        if (refreshToken) {
          try {
            await supabase.auth.setSession({ access_token: token, refresh_token: refreshToken });
          } catch {
            // Non-fatal — sessionStorage already has a usable token for this hour.
          }
        }
      }
      
      return { 
        success: true, 
        token,
        message: responseMessage
      };
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Registration and invitation acceptance failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    fetchInvitation,
    registerAndAcceptInvitation,
    isLoading,
    error
  };
};
